import { useMutation } from '@apollo/client';

import { mapOverlappingActivitiesErrors } from 'common/OverlappingActivitiesBanner/helpers/overlappingActivities';
import {
	WA_AUXILIARY_ERROR_RESPONSE,
	WA_AUXILIARY_SHIFT_TIME_TOO_LONG_ERROR,
	WA_AUXILIARY_SUCCESS_RESPONSE,
	WA_OVERLAPPING_ACTIVITIES_RESPONSE,
} from 'constants/activityTypes';
import { standardError, standardSuccess } from 'global/commonMessages';
import { notificationStore } from 'global/NotificationStore';

import { CREATE_AUXILIARY_ASSIGNMENT } from './gql/createAuxiliaryWorkAssignment';

/**
 * Pure network hook for creating auxiliary assignments.
 * No RHF, no overlap state, no page stores. Normalizes outcomes.
 *
 * Call pattern:
 *   const { submit, loading } = useCreateAuxiliaryAssignment();
 *   const result = await submit(formValues, { allowToDeleteOverlaps });
 *
 * @returns {{
 *   submit: (values: object, opts?: { allowToDeleteOverlaps?: boolean }) => Promise<
 *     | { kind: 'success', payload: any }
 *     | { kind: 'overlap', list: any[] }
 *     | { kind: 'shift-too-long' }
 *     | { kind: 'error', errors: any }
 *     | { kind: 'network-error', error: any }
 *     | { kind: 'unknown' }
 *   >,
 *   loading: boolean
 * }}
 */
export function useCreateAuxiliaryAssignment() {
	const [mutate, { loading }] = useMutation(CREATE_AUXILIARY_ASSIGNMENT, {
		fetchPolicy: 'network-only',
	});

	const submit = async (
		formValues,
		{ overlappingActivityAction = null, delayMinutes = null } = {}
	) => {
		try {
			const variables = { newAssignment: formValues };
			if (overlappingActivityAction) {
				variables.overlappingActivityAction = overlappingActivityAction;
			}
			if (delayMinutes != null && overlappingActivityAction === 'delay') {
				variables.delayMinutes = delayMinutes;
			}

			const { data } = await mutate({ variables });

			const resp = data?.saveAuxiliaryWorkAssignment;
			if (!resp) {
				notificationStore.setNotification(standardError);
				return { kind: 'unknown' };
			}

			if (resp.__typename === WA_OVERLAPPING_ACTIVITIES_RESPONSE) {
				return {
					kind: 'overlap',
					list: resp.overlappingActivities,
					minDelayMinutes: resp.minDelayMinutes,
					maxDelayMinutes: resp.maxDelayMinutes,
				};
			}

			if (resp.__typename === WA_AUXILIARY_ERROR_RESPONSE) {
				if (resp.errors?.includes(WA_AUXILIARY_SHIFT_TIME_TOO_LONG_ERROR)) {
					return { kind: 'shift-too-long' };
				}

				const mapped = mapOverlappingActivitiesErrors(resp.errors);
				notificationStore.setNotification(mapped);
				return { kind: 'error', errors: mapped };
			}

			if (resp.__typename === WA_AUXILIARY_SUCCESS_RESPONSE) {
				notificationStore.setNotification(standardSuccess);
				return { kind: 'success', payload: resp };
			}

			notificationStore.setNotification(standardError);
			return { kind: 'unknown' };
		} catch (e) {
			notificationStore.setNotification(standardError);
			return { kind: 'network-error', error: e };
		}
	};

	return { submit, loading };
}
